import { GAME_W, GAME_H, CELL_W, CELL_H, SPACE_X, SPACE_Y, GRID_ALPHA } from "./data";
import { landscape } from "./terrain";
import { copter } from "./copter";
import { background } from "./background";
import { hud } from "./hud";
import { curtains } from "./curtains";
import { lcd } from "./lcd";
import { particles } from "./effects";
import { menu, levelCompleteScreenObj, gameOverScreen, zoneBanner, startScreen } from "./update";
import { bullets, birdsFlocks, coins, pickups } from "./entities";
import { turrets } from "./turret";
import { getImage } from "./images";
import { Asset } from "assets.gen";
import { shaker } from "./shaker";
import { camera } from "./camera";
import type { Layout } from "lib/layout";

type RC = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

let offscreen: OffscreenCanvas;
let offCtx: OffscreenCanvasRenderingContext2D;

export function initRender(): void {
  offscreen = new OffscreenCanvas(GAME_W, GAME_H);
  offCtx = offscreen.getContext("2d")!;
  offCtx.imageSmoothingEnabled = false;
}

export function render(displayCtx: CanvasRenderingContext2D, layout: Layout): void {
  if (!offscreen) initRender();

  offCtx.clearRect(0, 0, GAME_W, GAME_H);

  if (shaker._offsetX !== 0 || shaker._offsetY !== 0) {
    offCtx.save();
    offCtx.translate(shaker._offsetX, shaker._offsetY);
  }

  background.render(offCtx as RC);
  renderTerrain();
  renderCoins();
  renderPickups();
  renderTurrets();
  renderBullets();
  renderBirds();
  renderCopter();
  renderParticles();

  if (shaker._offsetX !== 0 || shaker._offsetY !== 0) {
    offCtx.restore();
  }

  hud.render(offCtx as RC);
  menu.render(offCtx as RC);
  startScreen.render(offCtx as RC);
  levelCompleteScreenObj.render(offCtx as RC);
  gameOverScreen.render(offCtx as RC);
  zoneBanner.render(offCtx as RC);
  curtains.render(offCtx as RC);

  if (lcd.motionBlurEnabled && lcd.motionBlurAlpha < 1.0) {
    lcd.applyMotionBlur(offCtx, GAME_W, GAME_H);
  }
  if (lcd.grainEnabled && lcd.grainDepth > 0) {
    lcd.applyGrain(offCtx, GAME_W, GAME_H);
  }

  renderToDisplay(displayCtx, layout);
}

function renderTerrain(): void {
  if (!landscape._frame) return;
  offCtx.drawImage(landscape._frame, 0, 0);
}

function renderCoins(): void {
  const img = getImage(Asset.gfx_coin);
  if (!img) return;
  for (const coin of coins) {
    offCtx.drawImage(img, (coin.x - camera.x) | 0, coin.y | 0, coin.w, coin.h);
  }
}

function renderPickups(): void {
  const heartImg = getImage(Asset.gfx_heart);
  if (!heartImg) return;

  for (const pickup of pickups) {
    const sx = (pickup.x - camera.x) | 0;
    const sy = pickup.y | 0;
    if (pickup.type === "max_hp") {
      offCtx.drawImage(heartImg, 0, 0, 7, 7, sx, sy, pickup.w, pickup.h);
      offCtx.globalAlpha = 0.3;
      offCtx.fillStyle = "#ffff0077";
      offCtx.beginPath();
      offCtx.arc(sx + pickup.w * 0.5, sy + pickup.h * 0.5, 5, 0, Math.PI * 2);
      offCtx.fill();
    } else {
      offCtx.drawImage(heartImg, 0, 0, 7, 7, sx, sy, pickup.w, pickup.h);
      offCtx.globalAlpha = 0.25;
      offCtx.fillStyle = "#00ff4477";
      offCtx.beginPath();
      offCtx.arc(sx + pickup.w * 0.5, sy + pickup.h * 0.5, 4, 0, Math.PI * 2);
      offCtx.fill();
    }
  }
}

function renderTurrets(): void {
  const towerImg = getImage(Asset.gfx_turret_tower);
  const gunImg = getImage(Asset.gfx_turret_gun);
  if (!towerImg || !gunImg) return;

  for (const turret of turrets) {
    const sx = (turret.x - camera.x) | 0;
    const sy = turret.y | 0;
    offCtx.drawImage(towerImg, sx, turret.y);
    offCtx.save();
    offCtx.translate(sx + turret.cx, sy + turret.cy);
    offCtx.rotate(turret.angle);
    offCtx.drawImage(gunImg, 0, -2);
    offCtx.restore();
  }
}

function renderBullets(): void {
  for (const bullet of bullets) {
    const bx = (bullet.x - camera.x) | 0;
    const by = bullet.y | 0;
    offCtx.globalAlpha = 0.15;
    offCtx.fillStyle = "#ff4444";
    offCtx.beginPath();
    offCtx.arc(bx, by, 5, 0, Math.PI * 2);
    offCtx.fill();
    offCtx.globalAlpha = 0.35;
    offCtx.fillStyle = "#ff6644";
    offCtx.beginPath();
    offCtx.arc(bx, by, 3, 0, Math.PI * 2);
    offCtx.fill();
    offCtx.globalAlpha = 0.85;
    offCtx.fillStyle = "#ff0000";
    offCtx.fillRect(bx - 1, by - 1, 2, 2);
  }
  offCtx.globalAlpha = 1;
}

function renderBirds(): void {
  const img = getImage(Asset.gfx_bird);
  if (!img) return;

  for (const flock of birdsFlocks) {
    for (let i = 0; i < 3; i++) {
      const frame = (flock._t * 10) % 2 < 1 ? 0 : 3;
      const bx = ((flock.x - camera.x) | 0) + i * 3;
      const by = (flock.y | 0) + (flock._waveOffset[i] ?? 0);
      offCtx.drawImage(img, frame, 0, 3, 3, bx, by, 3, 3);
    }
  }
}

function renderCopter(): void {
  if (!copter.visible) return;
  const img = getImage(Asset.gfx_copter);
  if (!img) return;

  const frameW = 12;
  const frameH = 10;
  const frame = ((copter._t * 15) | 0) % 5;
  const sx = frame * frameW;

  const screenX = (copter.wx - camera.x) | 0;
  const screenY = (copter.y - 0) | 0;

  offCtx.save();
  offCtx.translate(screenX + frameW * 0.5, screenY + frameH * 0.5);
  offCtx.rotate((copter.angle * Math.PI) / 180);

  offCtx.globalAlpha = copter.flicker ? 0.2 : 1;
  offCtx.drawImage(img, sx, 0, frameW, frameH, -frameW * 0.5, -frameH * 0.5, frameW, frameH);
  offCtx.restore();
}

function renderParticles(): void {
  for (const p of particles) {
    offCtx.globalAlpha = p.life / p.maxLife;
    offCtx.fillStyle = p.color;
    const r = p.size;
    const x = (p.x - camera.x - r / 2) | 0;
    const y = (p.y - r / 2) | 0;
    offCtx.fillRect(x, y, r, r);
  }
  offCtx.globalAlpha = 1;
}

function renderToDisplay(displayCtx: CanvasRenderingContext2D, _layout: Layout): void {
  const cw = displayCtx.canvas.width;
  const ch = displayCtx.canvas.height;

  displayCtx.fillStyle = "#000000";
  displayCtx.fillRect(0, 0, cw, ch);

  const cellTotalW = CELL_W + SPACE_X;
  const cellTotalH = CELL_H + SPACE_Y;
  const displayW = GAME_W * cellTotalW;
  const displayH = GAME_H * cellTotalH;
  const scale = Math.min(cw / displayW, ch / displayH);
  const drawW = displayW * scale;
  const drawH = displayH * scale;
  const ox = (cw - drawW) * 0.5;
  const oy = (ch - drawH) * 0.5;

  displayCtx.imageSmoothingEnabled = false;
  displayCtx.drawImage(offscreen, ox, oy, drawW, drawH);

  displayCtx.globalAlpha = GRID_ALPHA;
  const gw = cellTotalW;
  const gh = cellTotalH;

  displayCtx.fillStyle = "#000000";
  for (let row = 0; row < GAME_H; row++) {
    const gy = oy + row * gh * scale + CELL_H * scale;
    displayCtx.fillRect(ox, gy, drawW, SPACE_Y * scale);
  }
  for (let col = 0; col < GAME_W; col++) {
    const gx = ox + col * gw * scale + CELL_W * scale;
    displayCtx.fillRect(gx, oy, SPACE_X * scale, drawH);
  }
  displayCtx.globalAlpha = 1;
}
